/* breadcrumbSchema.js
   -------------------
   Given a route path, produce a Schema.org BreadcrumbList JSON-LD
   object. Used by <PageBreadcrumbs /> and standalone schema injections.

   Strategy: a small static map for known routes (so labels match the
   actual page titles, not slug-derived guesses). For unknown routes
   the breadcrumb degrades to just "Home".

   Schema.org BreadcrumbList docs:
   https://schema.org/BreadcrumbList
*/

const SITE_ORIGIN = "https://www.british-sign.co.uk";

// Map of route paths to breadcrumb trails. Each trail is an array of
// { name, path } items. The first item is always "Home" (path "/").
//
// Trails are short on purpose – Google's breadcrumb rich results
// suppress crumbs deeper than 3-4 anyway, and the IA on the site is
// genuinely flat (no deep subfolders).
const TRAILS = {
  "/enrol":                                  ["course"],
  "/groups":                                 ["course", "groups"],
  "/what-is-bsl":                            ["learn", "what-is-bsl"],
  "/where-can-i-learn-bsl":                  ["learn", "where-to-learn-bsl"],
  "/left-handed-signing":                    ["learn", "left-handed-signing"],
  "/deaf-community":                         ["learn", "deaf-community"],
  "/start-here":                             ["start-here"],
  "/free-resources":                        ["free-resources"],
  "/bsl-family-signs":                       ["free-resources", "bsl-family-signs"],
  "/bsl-greetings-signs":                    ["free-resources", "bsl-greetings-signs"],
  "/bsl-fingerspelling-alphabet-chart":      ["free-resources", "alphabet-chart"],
  "/bsl-vocabulary-sheet-creator":           ["free-resources", "vocab-sheet-creator"],
  "/fingerspelling-word-search-maker":       ["free-resources", "word-search-maker"],
  "/greetings-card-creator":                 ["free-resources", "greetings-card-creator"],
  "/print-a-word-in-fingerspelling":         ["free-resources", "print-a-word"],
  "/fingerspell":                            ["free-tools", "fingerspell"],
  "/fingerspell/quiz":                       ["free-tools", "fingerspell", "quiz"],
  "/fingerspell/leaderboard":                ["free-tools", "fingerspell", "leaderboard"],
  "/signspell":                              ["free-tools", "signspell"],
  "/daily":                                  ["free-tools", "daily"],
  "/daily-bsl-word-guess":                   ["free-tools", "daily-word-guess"],
  "/sign-memory":                            ["free-tools", "sign-memory"],
  "/bsl-sign-quiz":                          ["free-tools", "bsl-sign-quiz"],
  "/bsl-dictionary":                         ["bsl-dictionary"],
  "/sign-of-the-day":                        ["bsl-dictionary", "sign-of-the-day"],
  "/lesson-plans":                           ["for-teachers", "lesson-plans"],
  "/lesson-plan-say-it-without-speaking":    ["for-teachers", "lesson-plans", "say-it-without-speaking"],
  "/lesson-plan-fingerspelling-through-play":["for-teachers", "lesson-plans", "fingerspelling-through-play"],
  "/for-schools":                            ["for-teachers", "for-schools"],
  "/for-libraries":                          ["for-teachers", "for-libraries"],
  "/for-tutors":                             ["for-teachers", "for-tutors"],
  "/for-scouts-guides":                      ["for-teachers", "for-scouts-guides"],
  "/sign-to-me":                             ["sign-to-me"],
  "/customer-reviews":                       ["about", "customer-reviews"],
  "/carbon-footprint":                       ["about", "carbon-footprint"],
  "/sister-sites":                           ["about", "sister-sites"],
  "/contact":                                ["about", "contact"],
  "/accessibility":                          ["about", "accessibility"],
  "/privacy-policy":                         ["legal", "privacy-policy"],
  "/terms-conditions":                       ["legal", "terms-conditions"],
  "/refund-policy":                          ["legal", "refund-policy"],
  "/cookies-policy":                         ["legal", "cookies-policy"],
};

// Human-readable labels for each crumb id. These appear directly in
// the SERP breadcrumb display so write them naturally.
const LABELS = {
  "course":                       "Course",
  "groups":                       "For groups",
  "learn":                        "Learn BSL",
  "what-is-bsl":                  "What is BSL?",
  "where-to-learn-bsl":           "Where to learn BSL",
  "left-handed-signing":          "Left-handed signing",
  "deaf-community":               "Deaf community",
  "start-here":                   "Start here",
  "free-resources":               "Free resources",
  "bsl-family-signs":             "Family signs",
  "bsl-greetings-signs":          "Greetings signs",
  "alphabet-chart":               "Alphabet chart",
  "vocab-sheet-creator":          "Vocabulary sheet creator",
  "word-search-maker":            "Word search maker",
  "greetings-card-creator":       "Greetings card creator",
  "print-a-word":                 "Print a word",
  "free-tools":                   "Free tools",
  "fingerspell":                  "Fingerspell player",
  "quiz":                         "Quiz",
  "leaderboard":                  "Leaderboard",
  "signspell":                    "Signspell",
  "daily":                        "Daily practice",
  "daily-word-guess":             "Fingerspell Five",
  "sign-memory":                  "Sign memory",
  "bsl-sign-quiz":                "BSL sign quiz",
  "bsl-dictionary":               "BSL dictionary",
  "sign-of-the-day":              "Sign of the Day",
  "for-teachers":                 "For teachers",
  "lesson-plans":                 "Lesson plans",
  "say-it-without-speaking":      "Say it without speaking",
  "fingerspelling-through-play":  "Fingerspelling through play",
  "for-schools":                  "For schools",
  "for-libraries":                "For libraries",
  "for-tutors":                   "For tutors",
  "for-scouts-guides":            "For Scouts & Guides",
  "sign-to-me":                   "Sign to me",
  "about":                        "About",
  "customer-reviews":             "Customer reviews",
  "carbon-footprint":             "Carbon footprint",
  "sister-sites":                 "Sister sites",
  "contact":                      "Contact",
  "accessibility":                "Accessibility",
  "legal":                        "Legal",
  "privacy-policy":               "Privacy policy",
  "terms-conditions":             "Terms & conditions",
  "refund-policy":                "Refund policy",
  "cookies-policy":               "Cookies policy",
};

// Map crumb id -> path. For intermediate crumbs that don't have a
// dedicated landing page, point them at the closest sensible hub.
const PATHS = {
  "course":          "/enrol",
  "learn":           "/start-here",
  "free-resources":  "/free-resources",
  "free-tools":      "/free-resources",
  "for-teachers":    "/lesson-plans",
  "about":           "/",
  "legal":           "/privacy-policy",
  "bsl-dictionary":  "/bsl-dictionary",
};

/**
 * Build a Schema.org BreadcrumbList object for the given route.
 * @param {string} path - route pathname (e.g. "/for-schools")
 * @returns {object|null} JSON-LD object, or null if no trail is defined
 */
export const buildBreadcrumbSchema = (path) => {
  const trail = TRAILS[path];
  if (!trail || !trail.length) return null;

  const items = [
    { name: "Home", item: SITE_ORIGIN + "/" },
    ...trail.map((id, i) => {
      const isLeaf = i === trail.length - 1;
      const leafPath = isLeaf ? path : PATHS[id] || path;
      return {
        name: LABELS[id] || id,
        item: SITE_ORIGIN + leafPath,
      };
    }),
  ];

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
};
